import { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * In-session "Change password" card on /settings.
 * Backed by POST /api/auth/change-password — returns a fresh JWT which is
 * silently rotated into AuthContext so the user stays signed in.
 */

const RULES = [
  { id: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'num', label: 'A number', test: (p) => /\d/.test(p) },
  { id: 'sym', label: 'A symbol', test: (p) => /[^a-zA-Z0-9]/.test(p) },
];

// Backend requires len ≥ 8 + 3-of-4 character classes — keep the UI rule consistent.
const isStrongEnough = (p) => {
  if (p.length < 8) return false;
  const classes = [
    /[a-z]/.test(p),
    /[A-Z]/.test(p),
    /\d/.test(p),
    /[^a-zA-Z0-9]/.test(p),
  ].filter(Boolean).length;
  return classes >= 3;
};

const PasswordField = ({ id, label, value, onChange, autoComplete, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-1">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          data-testid={testid}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-500 hover:text-zinc-800"
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export const ChangePasswordSection = () => {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const ruleResults = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(next) })), [next]);
  const passedRules = ruleResults.filter((r) => r.ok).length;
  const strongEnough = isStrongEnough(next);
  const matches = next.length > 0 && next === confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit =
    current.length > 0 &&
    strongEnough &&
    matches &&
    !sameAsCurrent &&
    !busy;

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success('Password updated. We sent a confirmation email to your inbox.');
      reset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  // Strength-meter color/label
  const strengthValue = Math.min(100, (passedRules / RULES.length) * 100);
  const strengthColor =
    strengthValue >= 80 ? 'bg-emerald-500' :
    strengthValue >= 60 ? 'bg-indigo-500' :
    strengthValue >= 40 ? 'bg-amber-500' :
                          'bg-red-500';
  const strengthLabel =
    next.length === 0 ? '' :
    strengthValue >= 80 ? 'Strong' :
    strengthValue >= 60 ? 'Good' :
    strengthValue >= 40 ? 'Weak' :
                          'Very weak';

  return (
    <Card className="border-zinc-200" data-testid="change-password-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-indigo-600" />
          Change password
        </CardTitle>
        <CardDescription>
          Update the password you use to sign in. We'll email you a confirmation when it's done.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4 max-w-md" data-testid="change-password-form">
          <PasswordField
            id="cp-current"
            label="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            testid="change-password-current"
          />
          <PasswordField
            id="cp-new"
            label="New password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            testid="change-password-new"
          />

          {/* Strength meter */}
          {next.length > 0 && (
            <div data-testid="strength-meter">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">Strength</span>
                <span className={
                  strengthValue >= 80 ? 'text-emerald-600 font-medium' :
                  strengthValue >= 60 ? 'text-indigo-600 font-medium' :
                  strengthValue >= 40 ? 'text-amber-600 font-medium' :
                                        'text-red-600 font-medium'
                }>
                  {strengthLabel}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${strengthColor}`}
                  style={{ width: `${strengthValue}%` }}
                />
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                {ruleResults.map((r) => (
                  <li
                    key={r.id}
                    className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-emerald-600' : 'text-zinc-400'}`}
                  >
                    <CheckCircle2 className={`w-3.5 h-3.5 ${r.ok ? 'opacity-100' : 'opacity-40'}`} />
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <PasswordField
            id="cp-confirm"
            label="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            testid="change-password-confirm"
          />

          {confirm.length > 0 && !matches && (
            <p className="text-xs text-red-600" data-testid="change-password-mismatch">
              Passwords don't match.
            </p>
          )}
          {sameAsCurrent && (
            <p className="text-xs text-amber-600" data-testid="change-password-same">
              New password must be different from your current password.
            </p>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="change-password-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…
                </>
              ) : (
                'Update password'
              )}
            </Button>
            {(current || next || confirm) && !busy && (
              <Button
                type="button"
                variant="ghost"
                onClick={reset}
                data-testid="change-password-cancel"
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordSection;
